// ログイン・アカウント（メンバー）管理 API クライアント。
//
// ローカル開発（vite dev）では /api/* が存在せず index.html が返るため、
// content-type が JSON でなければ「API未提供」と判定して認証をスキップする
// （archiveApi.ts / masterApi.ts と同じ扱い）。

import { noteAuthRequired } from "./authSignal";

export type MemberRole = "admin" | "member";

export type Member = {
  email: string;
  name: string;
  org: string;
  role: MemberRole;
  createdAt: string;
  createdBy?: string | null;
  lastLoginAt?: string | null;
  disabled?: boolean;
};

// JSON を返す API かどうかを見てからパースする共通処理
async function jsonFetch<T>(input: string, init?: RequestInit): Promise<T | { apiUnavailable: true }> {
  const r = await fetch(input, init);
  noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
  const ct = r.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return { apiUnavailable: true };
  return (await r.json()) as T;
}

export type MeResult = {
  apiAvailable: boolean; // false＝ローカル開発など（ログイン機能なしで従来動作）
  authRequired: boolean; // false＝KV未設定でログインを有効化できない環境
  member: Member | null;
  // 認証の確認そのものが成功したか。false＝通信断・API未提供・サーバ内部エラー（member:null は「未ログイン」ではない）。
  // /api/auth/me はサーバ例外時に HTTP200 で ok:false を返すため、これを見ずに member:null を
  // 期限切れ扱いにすると、KV の一時障害で有効なセッションの全員がログイン画面に落ちる（Astra レビュー R2 2026-09-07）
  ok: boolean;
  error?: string;
};

export async function fetchMe(): Promise<MeResult> {
  try {
    const d = await jsonFetch<{ ok: boolean; authRequired?: boolean; member?: Member | null; error?: string }>(
      "/api/auth/me"
    );
    if ("apiUnavailable" in d) return { apiAvailable: false, authRequired: false, member: null, ok: false };
    return {
      apiAvailable: true,
      authRequired: d.authRequired !== false,
      member: d.member ?? null,
      ok: d.ok === true,
      error: d.error,
    };
  } catch (e) {
    // ネットワーク断など。ログインを要求せず従来動作にフォールバックすると
    // 保護が外れて見えるため、ここでは「認証必要・未ログイン」とはせず素通りさせる
    return { apiAvailable: false, authRequired: false, member: null, ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// タブに戻ったときの再確認で何をするか（AuthGate から呼ぶ。純粋関数なのでテストできる）。
//   restore … 確認に成功しログイン中（起動時の通信失敗から会員状態を復旧する。Astra R1）
//   expire  … 確認に成功し未ログイン（セッション切れ）→ ログイン画面へ
//   none    … 確認に失敗（通信断・サーバ例外）or KV未設定 → 今の状態を壊さない（Astra R2）
export function decideAuthRecheck(me: MeResult): "restore" | "expire" | "none" {
  if (!me.ok || !me.apiAvailable || !me.authRequired) return "none";
  return me.member ? "restore" : "expire";
}

// next: ログイン後に戻る画面（携帯の届出フォーム `/?entry=1` から入ったとき用）。
// サーバ側で許可リストに載っているものだけが採用される（オープンリダイレクト防止）。
export async function requestLogin(email: string, next?: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const d = await jsonFetch<{ ok: boolean; error?: string }>("/api/auth/request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, next }),
    });
    if ("apiUnavailable" in d) return { ok: false, error: "API未提供（ローカル環境）です。本番URLでお試しください。" };
    return d;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST" });
  } catch {
    // ignore
  }
}

export async function fetchMembers(): Promise<{ ok: boolean; members: Member[]; me?: Member | null; error?: string }> {
  try {
    const d = await jsonFetch<{ ok: boolean; members?: Member[]; me?: Member | null; error?: string }>("/api/members");
    if ("apiUnavailable" in d) return { ok: false, members: [], error: "API未提供（ローカル環境）です。" };
    return { ok: d.ok, members: d.members || [], me: d.me ?? null, error: d.error };
  } catch (e) {
    return { ok: false, members: [], error: e instanceof Error ? e.message : String(e) };
  }
}

export async function addMember(args: {
  email: string;
  name: string;
  org?: string;
  role?: MemberRole;
  invite?: boolean;
}): Promise<{ ok: boolean; member?: Member; invited?: boolean; inviteError?: string; error?: string }> {
  return post("/api/members/add", args);
}

export async function updateMember(args: {
  email: string;
  name?: string;
  org?: string;
  role?: MemberRole;
  disabled?: boolean;
}): Promise<{ ok: boolean; member?: Member; error?: string }> {
  return post("/api/members/update", args);
}

export async function removeMember(email: string): Promise<{ ok: boolean; error?: string }> {
  return post("/api/members/remove", { email });
}

async function post<T extends { ok: boolean; error?: string }>(url: string, body: unknown): Promise<T> {
  try {
    const d = await jsonFetch<T>(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if ("apiUnavailable" in d) return { ok: false, error: "API未提供（ローカル環境）です。" } as T;
    return d;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) } as T;
  }
}
